import { defineMessages, useIntl } from 'react-intl-next';

import {
	LIVE_PAGE_MODE_SWITCH_TO_EDIT,
	LIVE_PAGE_MODE_SWITCH_TO_VIEW,
	getShortcutString,
} from '@confluence/shortcuts';
import { getLogger } from '@confluence/logger';
import { confluenceLocalStorageInstance as localStorage, keys } from '@confluence/storage-manager';
import type { FlagsStateContainer } from '@confluence/flags';

export const i18n = defineMessages({
	livePagesEditModeFlagTitle: {
		id: 'live-pages-features.live-pages.edit-mode-flag.title.livedocs',
		defaultMessage: `You’re editing this live doc`,
		description:
			'Title for the flag that displays when a user changes a live doc to be in edit mode that will allow them to make any modifications to the content',
	},
	livePagesEditModeFlagDescription: {
		id: 'live-pages-features.live-pages.edit-mode-flag.description',
		defaultMessage: 'Tip: To switch to viewing only, use the keyboard shortcut {keyCombination}.',
		description:
			'This message appears when a user switches a live page to edit mode, indicating that editing is enabled. It provides a tip on how to switch back to view-only mode by using a keyboard shortcut',
	},
	livePagesViewModeFlagTitle: {
		id: 'live-pages-features.live-pages.view-mode-flag.title.livedocs',
		defaultMessage: `You’re viewing this live doc`,
		description:
			'Title for the flag that displays when a user changes a live doc to be in view only mode that will not allow them to make any modifications to the content',
	},
	livePagesViewModeFlagDescription: {
		id: 'live-pages-features.live-pages.view-mode-flag.description',
		defaultMessage: 'Tip: To start editing, select {keyCombination} on your keyboard.',
		description:
			'This message appears when a user switches a live page to view-only mode, indicating that editing is disabled. It provides a tip on how to re-enable editing by pressing a keyboard shortcut.',
	},
});

type LivePageMode = 'view' | 'edit';

export const useLivePageModeFlag = (flags: FlagsStateContainer) => {
	const intl = useIntl();

	const logger = getLogger('editor-mainbuttons-livepage-modechanger');

	const hasLivePageModeFlagBeenShown = (mode: LivePageMode): boolean => {
		try {
			const flagsShown = JSON.parse(
				localStorage.getItem(keys.LIVE_PAGES_MODE_SWITCHING_FLAG_SHOWN) || '{}',
			);
			return flagsShown[mode] === true;
		} catch (error) {
			logger.error`Error checking if live page mode flag has been shown for mode: "${mode}". Reason: ${error.message}`;

			return false;
		}
	};

	const setLivePageModeFlagAsShown = (mode: LivePageMode): void => {
		try {
			const flagsShown = JSON.parse(
				localStorage.getItem(keys.LIVE_PAGES_MODE_SWITCHING_FLAG_SHOWN) || '{}',
			);
			flagsShown[mode] = true;
			localStorage.setItem(keys.LIVE_PAGES_MODE_SWITCHING_FLAG_SHOWN, JSON.stringify(flagsShown));
		} catch (error) {
			logger.error`Error setting live page mode flag as shown for mode: "${mode}". Reason: ${error.message}`;
		}
	};

	const modeDetails = {
		view: {
			title: intl.formatMessage(i18n.livePagesViewModeFlagTitle),
			description: intl.formatMessage(i18n.livePagesViewModeFlagDescription, {
				keyCombination: getShortcutString(LIVE_PAGE_MODE_SWITCH_TO_EDIT, intl.formatMessage),
			}),
		},
		edit: {
			title: intl.formatMessage(i18n.livePagesEditModeFlagTitle),
			description: intl.formatMessage(i18n.livePagesEditModeFlagDescription, {
				keyCombination: getShortcutString(LIVE_PAGE_MODE_SWITCH_TO_VIEW, intl.formatMessage),
			}),
		},
	};

	const showLivePageModeFlag = (newMode: LivePageMode) => {
		const selectedModeDetails = modeDetails[newMode];
		if (selectedModeDetails) {
			if (!hasLivePageModeFlagBeenShown(newMode)) {
				const { title, description } = selectedModeDetails;
				const flagId = 'live-pages-mode-flag';

				// hide any flags that are currently showing
				void flags.hideFlag(flagId);
				void flags.showFlag({
					id: flagId,
					title,
					description,
					close: 'auto',
				});
				setLivePageModeFlagAsShown(newMode);
			}
		}
	};

	return { showLivePageModeFlag };
};
